/**
 * Profile Overlay — web surface profile view/edit.
 *
 * Opens as a full-page overlay over the landing page.
 * Shows and edits identity, BusinessProfile, and BrandProfile.
 * Persists to Firestore when syncEnabled.
 */

import { updateProfile, updateEmail, sendPasswordResetEmail } from 'firebase/auth'
import { auth } from './firebase.js'
import { getProfile, saveProfile } from './profile-service.js'
import { callHostedTool, ensureActorForUser, listActorWorkspaces } from './actor-service.js'
import { setSafeHtml } from './safe-html.js'

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface ProfileBusinessData {
  businessName?: string
  businessCategory?: string
  productDescription?: string
  targetAudience?: string
  monthlyAdBudget?: string
  websiteUrl?: string
}

interface ProfileBrandData {
  brandName?: string
  industry?: string
  targetAudience?: string
  tone?: string
  offer?: string
  differentiator?: string
  website?: string
}

interface ProfileData {
  uid?: string
  displayName?: string | null
  email?: string | null
  photoURL?: string | null
  syncEnabled?: boolean
  businessProfile?: ProfileBusinessData | null
  brandProfile?: ProfileBrandData | null
}

let currentUser: { uid: string; displayName?: string | null; email?: string | null; photoURL?: string | null } | null = null
let profile: ProfileData | null = null
let _isEditing = false
let actor: Record<string, unknown> | null = null
let workspaceIds: string[] = []

// ---------------------------------------------------------------------------
// Overlay build
// ---------------------------------------------------------------------------

function buildProfileOverlay() {
  const el = document.createElement('div')
  el.id = 'profile-overlay'
  el.className = 'profile-overlay'
  el.setAttribute('role', 'dialog')
  el.setAttribute('aria-modal', 'true')
  el.setAttribute('aria-label', 'Your profile')

  setSafeHtml(el, `
    <div class="profile-sheet">
      <button class="profile-close" id="profile-close" aria-label="Close profile">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </button>

      <div class="profile-scroll">

        <!-- Header -->
        <div class="profile-hd">
          <div class="profile-avatar" id="profile-avatar">
            <span class="profile-avatar-initials" id="profile-avatar-inner">?</span>
          </div>
          <div class="profile-hd-meta">
            <div class="profile-hd-name" id="profile-hd-name">—</div>
            <div class="profile-hd-email" id="profile-hd-email">—</div>
          </div>
          <div class="profile-hd-actions">
            <button class="profile-btn" id="profile-edit-btn">Edit</button>
            <div class="profile-edit-actions hidden" id="profile-edit-actions">
              <button class="profile-btn profile-btn--primary" id="profile-save-btn">Save</button>
              <button class="profile-btn" id="profile-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>

        <div id="profile-error" class="profile-error hidden"></div>
        <div id="profile-loading" class="profile-loading">Loading your profile…</div>

        <div id="profile-content" class="profile-content hidden">

          <!-- Account -->
          <section class="profile-section">
            <div class="profile-section-title">Account</div>
            <div class="profile-row">
              <span class="profile-row-label">Display name</span>
              <span class="profile-row-value" id="profile-display-name">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Email</span>
              <span class="profile-row-value" id="profile-email">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Password</span>
              <span class="profile-row-value">
                <button class="profile-link-btn" id="profile-reset-btn">Send reset email</button>
              </span>
            </div>
          </section>

          <!-- Business -->
          <section class="profile-section">
            <div class="profile-section-title">Business Profile</div>
            <div class="profile-row">
              <span class="profile-row-label">Business name</span>
              <span class="profile-row-value" id="profile-business-name">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Category</span>
              <span class="profile-row-value" id="profile-business-category">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Description</span>
              <span class="profile-row-value" id="profile-business-description">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Target audience</span>
              <span class="profile-row-value" id="profile-business-audience">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Monthly ad budget</span>
              <span class="profile-row-value" id="profile-business-budget">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Website</span>
              <span class="profile-row-value" id="profile-business-website">—</span>
            </div>
          </section>

          <!-- Brand -->
          <section class="profile-section">
            <div class="profile-section-title">Brand Identity</div>
            <div class="profile-row">
              <span class="profile-row-label">Brand name</span>
              <span class="profile-row-value" id="profile-brand-name">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Industry</span>
              <span class="profile-row-value" id="profile-brand-industry">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Tone</span>
              <span class="profile-row-value" id="profile-brand-tone">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Core offer</span>
              <span class="profile-row-value" id="profile-brand-offer">—</span>
            </div>
            <div class="profile-row">
              <span class="profile-row-label">Differentiator</span>
              <span class="profile-row-value" id="profile-brand-differentiator">—</span>
            </div>
          </section>

          <!-- Sync -->
          <section class="profile-section">
            <div class="profile-section-title">Data Sync</div>
            <div class="profile-sync-row">
              <input type="checkbox" class="profile-sync-toggle" id="profile-sync-toggle" checked />
              <div>
                <div class="profile-sync-label">Save profile to cloud</div>
                <div class="profile-sync-desc">Syncs your profile data across devices via your Firebase account.</div>
              </div>
            </div>
          </section>

          <!-- Workspace -->
          <section class="profile-section">
            <div class="profile-section-title">Workspace Access</div>
            <div class="profile-row profile-row--stack">
              <span class="profile-row-label">Switch workspace</span>
              <div class="profile-workspace-controls">
                <select id="profile-workspace-select" class="profile-workspace-select"></select>
                <button class="profile-btn" id="profile-open-workspace-btn" type="button">Open workspace</button>
              </div>
              <div class="profile-inline-note">Choose where the hosted portal should open for this account.</div>
            </div>
            <div class="profile-row profile-row--stack">
              <span class="profile-row-label">Invite teammate</span>
              <div class="profile-invite-grid">
                <select id="profile-invite-role" class="profile-workspace-select">
                  <option value="operator">Operator</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="observer">Observer</option>
                </select>
                <select id="profile-invite-expiry" class="profile-workspace-select">
                  <option value="86400000">24 hours</option>
                  <option value="604800000" selected>7 days</option>
                  <option value="2592000000">30 days</option>
                </select>
                <input id="profile-invite-uses" class="profile-field-input" type="number" min="1" step="1" value="1" />
              </div>
              <div class="profile-workspace-controls">
                <button class="profile-btn" id="profile-create-invite-btn" type="button">Create invite</button>
              </div>
              <div id="profile-invite-result" class="profile-invite-result hidden"></div>
            </div>
          </section>

        </div><!-- /profile-content -->
      </div><!-- /profile-scroll -->

      <div id="profile-toast" class="profile-toast hidden"></div>
    </div>
  `)

  return el
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

export function openProfile(user: { uid: string; displayName?: string | null; email?: string | null; photoURL?: string | null }) {
  if (document.getElementById('profile-overlay')) return
  currentUser = user

  const overlay = buildProfileOverlay()
  document.body.appendChild(overlay)
  document.body.style.overflow = 'hidden'
  requestAnimationFrame(() => overlay.classList.add('profile-overlay--visible'))

  overlay.querySelector('#profile-close')!.addEventListener('click', closeProfile)
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeProfile() })
  document.addEventListener('keydown', handleEscape)

  overlay.querySelector('#profile-edit-btn')!.addEventListener('click', enterEditMode)
  overlay.querySelector('#profile-cancel-btn')!.addEventListener('click', exitEditMode)
  overlay.querySelector('#profile-save-btn')!.addEventListener('click', handleSave)
  overlay.querySelector('#profile-reset-btn')!.addEventListener('click', handlePasswordReset)
  overlay.querySelector('#profile-open-workspace-btn')?.addEventListener('click', handleOpenWorkspace)
  overlay.querySelector('#profile-create-invite-btn')?.addEventListener('click', () => void handleCreateInvite())
  overlay.querySelector('#profile-sync-toggle')!.addEventListener('change', async (e) => {
    const target = e.target as HTMLInputElement
    if (profile && currentUser && profile.syncEnabled !== target.checked) {
      profile.syncEnabled = target.checked
      if (target.checked) await saveProfile(currentUser.uid, { syncEnabled: true })
    }
  })

  loadProfile()
}

function closeProfile() {
  const overlay = document.getElementById('profile-overlay')
  if (!overlay) return
  overlay.classList.remove('profile-overlay--visible')
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleEscape)
  _isEditing = false
  setTimeout(() => overlay.remove(), 300)
}

function handleEscape(e: KeyboardEvent) {
  if (e.key === 'Escape') closeProfile()
}

// ---------------------------------------------------------------------------
// Load
// ---------------------------------------------------------------------------

async function loadProfile() {
  if (!currentUser) return

  document.getElementById('profile-loading')?.classList.remove('hidden')
  document.getElementById('profile-content')?.classList.add('hidden')

  ;[profile, actor] = await Promise.all([
    getProfile(currentUser.uid),
    ensureActorForUser(currentUser)
  ])
  if (!profile) {
    profile = {
      uid: currentUser.uid,
      displayName: currentUser.displayName || '',
      email: currentUser.email || '',
      photoURL: currentUser.photoURL || '',
      syncEnabled: true,
      businessProfile: null,
      brandProfile: null
    }
  }
  workspaceIds = actor?.actorId ? await listActorWorkspaces(String(actor.actorId)).catch(() => []) : []

  document.getElementById('profile-loading')?.classList.add('hidden')
  document.getElementById('profile-content')?.classList.remove('hidden')
  renderProfile()
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderProfileAvatar() {
  if (!profile) return
  const avatarEl = document.getElementById('profile-avatar')
  if (!avatarEl) return

  avatarEl.replaceChildren()
  const safePhotoURL = getSafeHttpUrl(profile.photoURL)
  if (safePhotoURL) {
    const image = document.createElement('img')
    image.src = safePhotoURL
    image.alt = ''
    image.className = 'profile-avatar-img'
    avatarEl.append(image)
    return
  }

  const avatarInner = document.createElement('span')
  avatarInner.className = 'profile-avatar-initials'
  avatarInner.id = 'profile-avatar-inner'
  const initials = (profile.displayName || profile.email || '?')
    .split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
  avatarInner.textContent = initials
  avatarEl.append(avatarInner)
}

function renderProfileIdentity() {
  if (!profile) return
  setText('profile-hd-name', profile.displayName || profile.email || '—')
  setText('profile-hd-email', profile.email || '—')
  setVal('profile-display-name', profile.displayName || '—')
  setVal('profile-email', profile.email || '—')
}

function renderBusinessProfile() {
  if (!profile) return
  const bp = profile.businessProfile || {}
  setVal('profile-business-name', bp.businessName || '—')
  setVal('profile-business-category', bp.businessCategory || '—')
  setVal('profile-business-description', bp.productDescription || '—')
  setVal('profile-business-audience', bp.targetAudience || '—')
  setVal('profile-business-budget', bp.monthlyAdBudget || '—')
  setVal('profile-business-website', bp.websiteUrl || '—')
}

function renderBrandProfile() {
  if (!profile) return
  const brand = profile.brandProfile || {}
  setVal('profile-brand-name', brand.brandName || '—')
  setVal('profile-brand-industry', brand.industry || '—')
  setVal('profile-brand-tone', brand.tone || '—')
  setVal('profile-brand-offer', brand.offer || '—')
  setVal('profile-brand-differentiator', brand.differentiator || '—')
}

function renderSyncToggle() {
  if (!profile) return
  const toggle = document.getElementById('profile-sync-toggle') as HTMLInputElement | null
  if (toggle) toggle.checked = profile.syncEnabled !== false
}

function renderProfile() {
  if (!profile) return
  renderProfileAvatar()
  renderProfileIdentity()
  renderBusinessProfile()
  renderBrandProfile()
  renderSyncToggle()
  renderWorkspaceAccess()
}

function setText(id: string, value: string) {
  const el = document.getElementById(id)
  if (el) el.textContent = value
}

function setVal(id: string, value: string) {
  const el = document.getElementById(id)
  if (!el) return
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    ;(el as HTMLInputElement | HTMLTextAreaElement).value = value === '—' ? '' : value
  } else {
    el.textContent = value
  }
}

function getSafeHttpUrl(value: unknown) {
  const candidate = String(value || '').trim()
  if (!candidate) return ''
  try {
    const url = new URL(candidate)
    if (url.protocol === 'https:' || url.protocol === 'http:') {
      return url.toString()
    }
    return ''
  } catch {
    return ''
  }
}

function renderWorkspaceAccess() {
  const select = document.getElementById('profile-workspace-select')
  if (select) {
    const workspaceOptions = workspaceIds.length > 0 ? workspaceIds : ['']
    const options = workspaceOptions.map((workspaceId) => {
      const option = document.createElement('option')
      option.value = workspaceId
      option.textContent = workspaceId || 'No workspace linked yet'
      return option
    })
    select.replaceChildren(...options)

    const currentWorkspace = new URLSearchParams(window.location.search).get('workspace') || workspaceIds[0] || ''
    ;(select as HTMLSelectElement).value = workspaceOptions.includes(currentWorkspace) ? currentWorkspace : (workspaceIds[0] || '')
  }
}

function createInviteCopyRow(value: string, label: string) {
  const row = document.createElement('div')
  row.className = 'profile-copy-row'

  const copyValue = document.createElement('span')
  copyValue.className = 'profile-copy-value'
  copyValue.textContent = value

  const button = document.createElement('button')
  button.className = 'profile-link-btn'
  button.type = 'button'
  button.dataset.profileCopy = value
  button.textContent = label

  row.append(copyValue, button)
  return row
}

function renderInviteResult(invite: Record<string, unknown>) {
  const result = document.getElementById('profile-invite-result')
  if (!result) return
  const inviteCode = String(invite?.inviteCode || '').trim()
  const inviteLink = String(invite?.inviteLink || '').trim()
  result.classList.remove('hidden')
  result.replaceChildren()

  const note = document.createElement('div')
  note.className = 'profile-inline-note'
  note.style.marginBottom = '8px'
  note.textContent = 'Invite created. Share the code or link.'
  result.append(note)

  if (inviteCode) result.append(createInviteCopyRow(inviteCode, 'Copy code'))
  if (inviteLink) result.append(createInviteCopyRow(inviteLink, 'Copy link'))

  result.querySelectorAll('[data-profile-copy]').forEach(btn => {
    btn.addEventListener('click', async (event) => {
      const el = event.currentTarget as HTMLElement | null
      if (!el) return
      const value = el.getAttribute('data-profile-copy') || ''
      if (!value) return
      await navigator.clipboard.writeText(value).catch(() => {})
      el.textContent = value.startsWith('http') ? 'Copied link' : 'Copied code'
      setTimeout(() => {
        el.textContent = value.startsWith('http') ? 'Copy link' : 'Copy code'
      }, 1200)
    })
  })
}

// ---------------------------------------------------------------------------
// Edit mode
// ---------------------------------------------------------------------------

function enterEditMode() {
  _isEditing = true
  document.getElementById('profile-edit-btn')?.classList.add('hidden')
  document.getElementById('profile-edit-actions')?.classList.remove('hidden')

  toInput('profile-display-name', 'text', 'Your display name')
  toInput('profile-email', 'email', 'your@email.com')
  toInput('profile-business-name', 'text', 'Business name')
  toInput('profile-business-category', 'text', 'e.g. E-commerce, SaaS')
  toInput('profile-business-description', 'text', 'What you offer')
  toInput('profile-business-audience', 'text', 'Who you target')
  toInput('profile-business-budget', 'text', 'e.g. $5,000 / month')
  toInput('profile-business-website', 'url', 'https://yoursite.com')
  toInput('profile-brand-name', 'text', 'Brand name')
  toInput('profile-brand-industry', 'text', 'Industry')
  toInput('profile-brand-tone', 'text', 'e.g. Warm, authoritative')
  toInput('profile-brand-offer', 'text', 'Core offer / value prop')
  toInput('profile-brand-differentiator', 'text', 'What sets you apart')
}

function exitEditMode() {
  _isEditing = false
  document.getElementById('profile-edit-btn')?.classList.remove('hidden')
  document.getElementById('profile-edit-actions')?.classList.add('hidden')
  renderProfile()
}

function toInput(id: string, type: string, placeholder: string) {
  const el = document.getElementById(id)
  if (!el || el.tagName === 'INPUT') return
  const current = el.textContent || ''
  const input = document.createElement('input')
  input.id = id
  input.className = 'profile-field-input'
  input.type = type
  input.placeholder = placeholder
  input.value = current === '—' ? '' : current
  el.replaceWith(input)
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

async function handleSave() {
  if (!currentUser || !profile) return

  const saveBtn = document.getElementById('profile-save-btn') as HTMLButtonElement | null
  const errorEl = document.getElementById('profile-error')
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…' }
  errorEl?.classList.add('hidden')

  try {
    const displayName = getInputVal('profile-display-name')
    const email = getInputVal('profile-email')

    if (displayName !== currentUser.displayName) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateProfile(currentUser as any, { displayName })
    }
    if (email && email !== currentUser.email) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateEmail(currentUser as any, email)
    }

    const bp = {
      businessName: getInputVal('profile-business-name'),
      businessCategory: getInputVal('profile-business-category'),
      productDescription: getInputVal('profile-business-description'),
      targetAudience: getInputVal('profile-business-audience'),
      monthlyAdBudget: getInputVal('profile-business-budget'),
      websiteUrl: getInputVal('profile-business-website')
    }

    const brand = {
      brandName: getInputVal('profile-brand-name'),
      industry: getInputVal('profile-brand-industry'),
      targetAudience: getInputVal('profile-business-audience'),
      tone: getInputVal('profile-brand-tone'),
      offer: getInputVal('profile-brand-offer'),
      differentiator: getInputVal('profile-brand-differentiator'),
      website: getInputVal('profile-business-website')
    }

    const syncEnabled = (document.getElementById('profile-sync-toggle') as HTMLInputElement | null)?.checked !== false
    profile = { ...profile, displayName, email, businessProfile: bp, brandProfile: brand, syncEnabled }

    if (syncEnabled) {
      await saveProfile(currentUser.uid, { displayName, email, businessProfile: bp, brandProfile: brand, syncEnabled })
    }

    setText('profile-hd-name', displayName || email || '—')
    setText('profile-hd-email', email || '—')

    exitEditMode()
    showToast('Profile saved')
  } catch (err) {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save' }
    if (errorEl) {
      errorEl.textContent = err instanceof Error ? err.message : 'Failed to save. Please try again.'
      errorEl.classList.remove('hidden')
    }
  }
}

function getInputVal(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value?.trim() || ''
}

async function handlePasswordReset() {
  if (!currentUser?.email) return
  try {
    await sendPasswordResetEmail(auth, currentUser.email)
    showToast('Password reset email sent')
  } catch {
    showToast('Could not send reset email')
  }
}

function handleOpenWorkspace() {
  const selectedWorkspace = (document.getElementById('profile-workspace-select') as HTMLSelectElement | null)?.value?.trim() || ''
  if (!selectedWorkspace) return
  const url = new URL(window.location.href)
  url.hash = '#operator'
  url.searchParams.set('workspace', selectedWorkspace)
  window.location.href = url.toString()
}

async function handleCreateInvite() {
  const selectedWorkspace = (document.getElementById('profile-workspace-select') as HTMLSelectElement | null)?.value?.trim() || ''
  if (!selectedWorkspace) {
    showToast('Pick a workspace first')
    return
  }
  const role = (document.getElementById('profile-invite-role') as HTMLSelectElement | null)?.value?.trim() || 'operator'
  const expiresInMs = Number((document.getElementById('profile-invite-expiry') as HTMLSelectElement | null)?.value || 604800000)
  const maxUses = Number((document.getElementById('profile-invite-uses') as HTMLSelectElement | null)?.value || 1)
  try {
    const invite = await callHostedTool('createWorkspaceInvite', {
      workspaceId: selectedWorkspace,
      role,
      expiresInMs,
      maxUses
    })
    renderInviteResult(invite)
    showToast('Invite created')
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Could not create invite')
  }
}

function showToast(msg: unknown) {
  const toast = document.getElementById('profile-toast')
  if (!toast) return
  toast.textContent = String(msg)
  toast.classList.remove('hidden')
  setTimeout(() => toast.classList.add('hidden'), 2800)
}
